import type { ColumnDef } from '@tanstack/react-table'

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import type { VisitRecord } from '@/features/visits/types'

const Unavailable = () => <span className="text-muted-foreground">-</span>

/** Parse a YYYY-MM-DD date as a local calendar date (no timezone shift). */
export function formatVisitDate(date: string | null): string | null {
  if (!date) return null
  const parts = date.split('-').map((p) => parseInt(p, 10))
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null
  const [year, month, day] = parts as [number, number, number]
  const parsed = new Date(year, month - 1, day)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/** v3 Visits table: 6 at-a-glance columns; the rest live in the details sheet. */
export const visitsColumns: ColumnDef<VisitRecord>[] = [
  {
    accessorKey: 'visit_date',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Visit Date" />
    ),
    enableHiding: false,
    cell: ({ row }) => (
      <div className="whitespace-nowrap">
        {formatVisitDate(row.original.visit_date) ?? <Unavailable />}
      </div>
    ),
  },
  {
    accessorKey: 'visit_type',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Visit Type" />
    ),
    cell: ({ row }) => (
      <div className="truncate">
        {row.original.visit_type || <Unavailable />}
      </div>
    ),
  },
  {
    accessorKey: 'program',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Program" />
    ),
    cell: ({ row }) => (
      <div className="truncate">{row.original.program || <Unavailable />}</div>
    ),
  },
  {
    accessorKey: 'location',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Location" />
    ),
    cell: ({ row }) => (
      <div className="truncate">{row.original.location || <Unavailable />}</div>
    ),
  },
  {
    accessorKey: 'primary_payer',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Primary Payer" />
    ),
    cell: ({ row }) => (
      <div className="truncate">
        {row.original.primary_payer || <Unavailable />}
      </div>
    ),
  },
  {
    accessorKey: 'is_billable',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Billable" />
    ),
    cell: ({ row }) => {
      const value = row.original.is_billable
      if (value === null || value === undefined) return <Unavailable />
      return <div className="whitespace-nowrap">{value ? 'Yes' : 'No'}</div>
    },
  },
]
